// ProductKitDetail.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProductGroupDetail.css'; // Giữ nguyên CSS
import { productGroupsAPI } from '../../utils/api';
import { getDisplayName, VariantCard, useImageLoader } from './ProductGroupDetailCommon'; // Import từ file common

const ProductKitDetail = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [productGroup, setProductGroup] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [hoveredVariant, setHoveredVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [searchTerm] = useState('');
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const getImageUrl = useImageLoader(); // Sử dụng hook từ file common

  useEffect(() => {
    loadProductGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const loadProductGroup = async () => {
    setIsLoading(true);
    try {
      const response = await productGroupsAPI.getProductGroupById(groupId);
      const group = response.data.data;
      setProductGroup(group);
      // For kits, often there's one main "variant" which is the kit itself, or a few options
      const firstInStock = group.preview_variants.find(v => v.inventory_qty > 0);
      setSelectedVariant(firstInStock || group.preview_variants[0]);
    } catch (error) {
      console.error('Error loading product group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setQuantity(1);
    setActiveImageIndex(0);
  };

  const handleAddToCart = () => {
    if (!selectedVariant) return;
    setAddingToCart(true);
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const existingIndex = cart.findIndex(item => item.sku === selectedVariant.sku);
      if (existingIndex >= 0) {
        cart[existingIndex].quantity += quantity;
      } else {
        cart.push({
          sku: selectedVariant.sku,
          name: productGroup.title,
          variant: selectedVariant.attribute || selectedVariant.size,
          price: parseFloat(selectedVariant.retail_price),
          quantity,
          image_url: getImageUrl(selectedVariant.hero_image),
          vendor_name: productGroup.vendor_name,
          combo_items: (productGroup.preview_variants || [])
            .map(item => item?.product_title)
            .filter(Boolean)
        });
        
        console.log(productGroup)
        console.log(selectedVariant)
      }
      localStorage.setItem('cart', JSON.stringify(cart));
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      alert(`Added ${quantity} item(s) to cart!`);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart. Please try again.');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= 1 && newQty <= 99) setQuantity(newQty);
  };

  /* The image shown in the hero area: hovered variant > selected variant > hero_image */
  const displayVariant = hoveredVariant || selectedVariant;

  /* Build gallery images for the selected variant */
  const galleryImages = useMemo(() => {
    if (!displayVariant) return [];
    const imgs = [];
    const seen = new Set();
    const addUnique = (url) => {
      if (url &&!seen.has(url)) { seen.add(url); imgs.push(url); }
    };

    // 1. Hero image first (admin-set product image)
    if (productGroup?.hero_image) {
      addUnique(getImageUrl(productGroup.hero_image));
    }
    // 2. Images from variant_images table
    if (displayVariant.images && displayVariant.images.length > 0) {
      displayVariant.images.forEach(img => addUnique(getImageUrl(img.image_url)));
    }
    // 3. Fall back to the single variant image_url
    if (displayVariant.image_url) {
      addUnique(getImageUrl(displayVariant.image_url));
    }
    return imgs;
  }, [displayVariant, productGroup, getImageUrl]);

  const heroSrc = galleryImages[activeImageIndex] || galleryImages[0] || getImageUrl(productGroup?.hero_image);
  const fallbackImage = getImageUrl(productGroup?.hero_image);

  /* Compute display names for all variants */
  const variantDisplayNames = useMemo(() => {
    if (!productGroup) return {};
    const allVariants = productGroup.preview_variants || [];
    const names = {};
    allVariants.forEach(v => {
      // fallback to sku if id is missing
      const key = v.id || v.sku;
      names[key] = getDisplayName(v, allVariants);
    });
    return names;
  }, [productGroup]);

  /* Extract unique sizes for the size selector (only when multiple sizes exist) */
  const availableSizes = useMemo(() => {
    if (!productGroup) return [];
    const variants = productGroup.preview_variants || [];
    const sizes = [...new Set(variants.map(v => v.size).filter(Boolean))];
    // Only show size selector if there are 2+ distinct sizes AND many variants
    return sizes.length >= 2 && variants.length > 10? sizes : [];
  }, [productGroup]);

  /* Auto-select first size when sizes are available */
  useEffect(() => {
    if (availableSizes.length > 0 &&!selectedSize) {
      setSelectedSize(availableSizes[0]);
    }
  }, [availableSizes, selectedSize]);

  /* When size changes, auto-select first in-stock variant of that size */
  useEffect(() => {
    if (!selectedSize ||!productGroup) return;
    const sizeVariants = productGroup.preview_variants.filter(v => v.size === selectedSize);
    const firstInStock = sizeVariants.find(v => v.inventory_qty > 0);
    if (firstInStock) setSelectedVariant(firstInStock);
    else if (sizeVariants.length > 0) setSelectedVariant(sizeVariants[0]);
  }, [selectedSize, productGroup]);

  /* Filter variants by size first, then by search */
  const filteredVariants = useMemo(() => {
    if (!productGroup) return [];
    let variants = productGroup.preview_variants || [];

    // Filter by selected size
    if (selectedSize && availableSizes.length > 0) {
      variants = variants.filter(v => v.size === selectedSize);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      variants = variants.filter(v => {
        const name = variantDisplayNames[v.id] || '';
        return name.toLowerCase().includes(q) ||
          (v.attribute || '').toLowerCase().includes(q) ||
          (v.size || '').toLowerCase().includes(q) ||
          (v.sku || '').toLowerCase().includes(q);
      });
    }

    return variants;
  }, [productGroup, searchTerm, selectedSize, availableSizes, variantDisplayNames]);

  /* Preload all variant images so hover preview is instant */
  useEffect(() => {
    if (!productGroup) return;
    const variants = productGroup.preview_variants || [];
    variants.forEach(v => {
      if (v.image_url) {
        const img = new Image();
        img.src = getImageUrl(v.image_url);
      }
    });
  }, [productGroup, getImageUrl]);

  if (isLoading) {
    return (
      <div className="product-group-detail loading">
        <div className="spinner"></div>
        <p>Loading kit product...</p>
      </div>
    );
  }

  if (!productGroup) {
    return (
      <div className="product-group-detail error">
        <h2>Kit Product not found</h2>
        <button onClick={() => navigate('/shop')} className="btn-back">Back to Shop</button>
      </div>
    );
  }

  const isOutOfStock = selectedVariant && selectedVariant.inventory_qty <= 0;

  return (
    <div className="product-group-detail">
      <div className="detail-container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span onClick={() => navigate('/shop')} className="breadcrumb-link">Shop</span>
          <span className="breadcrumb-separator">/</span>
          {productGroup.parent_category_name && (
            <>
              <span onClick={() => navigate(`/shop?category=${productGroup.parent_category_slug}`)} className="breadcrumb-link">{productGroup.parent_category_name}</span>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          {productGroup.category_name && (
            <>
              <span onClick={() => navigate(`/shop?category=${productGroup.category_slug}`)} className="breadcrumb-link">{productGroup.category_name}</span>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">{productGroup.title}</span>
        </div>

        <div className="detail-content">
          {/* ── Left: Image ── */}
          <div className="detail-image-section">
            <div className="main-image">
              {heroSrc? (
                <img src={heroSrc} alt={productGroup.title} />
              ) : (
                <div className="no-image">No Image Available</div>
              )}
              {/* KIT badge is present here for kit products */}
              {productGroup.group_type === 'kit' && (
                <span className="kit-badge-large">Kit</span>
              )}
              {hoveredVariant && (
                <div className="hover-label">
                  {variantDisplayNames[hoveredVariant.id] || hoveredVariant.attribute || 'Preview'}
                </div>
              )}
            </div>
            {galleryImages.length > 1 &&!hoveredVariant && (
              <div className="image-thumbnails">
                {galleryImages.map((src, idx) => (
                  <button
                    key={idx}
                    className={`thumbnail-btn ${activeImageIndex === idx? 'active' : ''}`}
                    onClick={() => setActiveImageIndex(idx)}
                  >
                    <img src={src} alt={`${productGroup.title} ${idx + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Right: Info ── */}
          <div className="detail-info-section">
            {productGroup.vendor_name && (
              <p className="detail-vendor">{productGroup.vendor_name}</p>
            )}
            <h1 className="detail-title">{productGroup.title}</h1>
            {productGroup.category_name && (
              <p className="detail-category">{productGroup.parent_category_name? `${productGroup.parent_category_name} › ` : ''}{productGroup.category_name}</p>
            )}
           <div className="detail-price">
              <span className="current-price">
                ${productGroup.min_price.toFixed(2)} {/* Giá ưu đãi */}
              </span>

              {productGroup.preview_variants && productGroup.preview_variants.length > 0 && (
                <span className="original-price-strikethrough">
                  ${productGroup.preview_variants
                    .reduce((sum, v) => sum + parseFloat(v.retail_price), 0)
                    .toFixed(2)} {/* Tổng giá gốc tất cả variants */}
                </span>
              )}
            </div>

            {/* ── Option Grid ── */}
            {/* Kits might have variants, so keep this logic for now */}
            {productGroup.preview_variants.length > 1 && (
              <div className="variant-selector">

                <div className="variant-grid">
                  {filteredVariants.map((variant) => (
                    <VariantCard
                      key={`${variant.id || variant.sku}`}
                      variant={variant}
                      isSelected={selectedVariant?.id === variant.id}
                      onSelect={handleVariantSelect}
                      onHover={() => setHoveredVariant(null)}
                      onHoverEnd={() => setHoveredVariant(null)}
                      getImageUrl={getImageUrl}
                      fallbackImage={fallbackImage}
                      displayName={variantDisplayNames[variant.id] || variant.sku || 'Option'}
                    />
                  ))}
                  {filteredVariants.length === 0 && (
                    <p className="variant-no-results">No options match "{searchTerm}"</p>
                  )}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="quantity-section">
              <h3>Quantity</h3>
              <div className="quantity-controls">
                <button className="qty-btn" onClick={() => handleQuantityChange(-1)} disabled={quantity <= 1}>−</button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    if (val >= 1 && val <= 99) setQuantity(val);
                  }}
                  min="1" max="99"
                />
                <button className="qty-btn" onClick={() => handleQuantityChange(1)} disabled={quantity >= 99}>+</button>
              </div>
            </div>

            {/* Stock */}
            {selectedVariant && (
              <div className="stock-status">
                {isOutOfStock? (
                  <span className="out-of-stock-text">Out of Stock</span>
                ) : selectedVariant.inventory_qty < 10? (
                  <span className="low-stock-text">Only {selectedVariant.inventory_qty} left in stock</span>
                ) : (
                  <span className="in-stock-text">In Stock</span>
                )}
              </div>
            )}

            {/* Add to Cart */}
            <button
              className="btn-add-to-cart"
              onClick={handleAddToCart}
              disabled={!selectedVariant || isOutOfStock || addingToCart}
            >
              {addingToCart? 'Adding...' : isOutOfStock? 'Out of Stock' : 'Add to Cart'}
            </button>

            {/* Details */}
            <div className="product-details">
              <h3>Product Details</h3>
              <ul>
                <li>
                  <strong>SKU:</strong> {productGroup.preview_variants.map(v => v.sku).join('-')}
                </li>
              </ul>
              {productGroup.description && (
                <div className="product-description">
                  <p>{productGroup.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductKitDetail;